import { lazy, Suspense, type ComponentType, type ReactNode } from 'react'
import { Route, Routes } from 'react-router-dom'

import { AdminShell } from '@/components/layout/AdminShell'
import { MODULES } from '@/config/modules'
import { RedirectIfSignedIn, RequireAdmin } from '@/features/auth/RequireAdmin'

import { NotFoundPage } from './NotFoundPage'
import { RouteFallback } from './RouteFallback'

/**
 * Every module page is code-split (`React.lazy`) — the spec caps the initial
 * bundle at 350 KB, and 25 modules cannot all live in it.
 */
const DashboardPage = lazy(() => import('@/features/dashboard/DashboardPage'))
const UsersPage = lazy(() => import('@/features/users/UsersPage'))
const BusinessesPage = lazy(() => import('@/features/businesses/BusinessesPage'))
const CategoriesPage = lazy(() => import('@/features/categories/CategoriesPage'))
const ServicesPage = lazy(() => import('@/features/services/ServicesPage'))
const TopicsPage = lazy(() => import('@/features/topics/TopicsPage'))
// A-29 · the Daily carousel's schedule. 🔴 IT EXISTS BECAUSE THE CLIENT REPORTED
// the cards missing on 12 Oct and the cause was twenty invisible empty days:
// `03-social.mjs` pinned all three cards to the date its seed ran. Nothing in
// this panel could show a schedule and nothing anywhere could author a card.
// ⚠️ It adds no migration, so "write a card and schedule it" is three statements
// with a compensating delete rather than a transaction — see `features/daily/data.ts`.
const DailyPage = lazy(() => import('@/features/daily/DailyPage'))
const RequestsPage = lazy(() => import('@/features/requests/RequestsPage'))
const ReviewsPage = lazy(() => import('@/features/reviews/ReviewsPage'))
const ModerationPage = lazy(() => import('@/features/moderation/ModerationPage'))
const VerificationPage = lazy(() => import('@/features/verification/VerificationPage'))
// A-26 · migrations 045/046. The ACCESS half of Docs/29 § 3, beside A-06 rather
// than inside it — the two are different systems.
const ClaimsPage = lazy(() => import('@/features/claims/ClaimsPage'))
// A-27 · the other half of Docs/29 § 2 — businesses awaiting review, and the only
// legitimate path to `status = 'active'` (migration 048).
const SubmissionsPage = lazy(() => import('@/features/submissions/SubmissionsPage'))
const TemplatesPage = lazy(() => import('@/features/templates/TemplatesPage'))
const BroadcastsPage = lazy(() => import('@/features/broadcasts/BroadcastsPage'))
const TranslationsPage = lazy(() => import('@/features/translations/TranslationsPage'))
// Migrations 030–034 (22 Sep) gave these four a table for the first time.
const CmsPage = lazy(() => import('@/features/cms/CmsPage'))
const SettingsPage = lazy(() => import('@/features/settings/SettingsPage'))
const FeatureFlagsPage = lazy(() => import('@/features/feature-flags/FeatureFlagsPage'))
const AuditLogPage = lazy(() => import('@/features/audit-log/AuditLogPage'))
// A-21 · migrations 035/036 (22 Sep).
const AnalyticsPage = lazy(() => import('@/features/analytics/AnalyticsPage'))
// A-22 · the ONLY module in this panel that talks to an Edge Function rather than
// to a table: `admin_users` has no write policy and RLS filters, so a
// table-backed version would report success for grants that never happened.
const AdminTeamPage = lazy(() => import('@/features/admin-team/AdminTeamPage'))
// A-19 · migration 052. Never asked for by the client and never refused either —
// built under the Docs/15 Q81 rule, deliberately small.
const SupportPage = lazy(() => import('@/features/support/SupportPage'))
// A-20 · migrations 057/058. Page 2 of the client's § 2.3 Support interface,
// restored 6 Oct after being deleted on 1 Oct on a misreading of his scope.
// 🔴 It records a disagreement and its verdict. It moves no money: there is no
// escrow in this schema and the module must never imply one (Docs/15 Q96).
const DisputesPage = lazy(() => import('@/features/disputes/DisputesPage'))
// A-11 · migrations 066/067/068. The price list, admin-editable per hard rule 4.
// ⚠️ Its table is DELIBERATELY EMPTY — no price or trial length has been agreed —
// and the editor carries no defaults, so an empty field stays an empty field.
const PlansPage = lazy(() => import('@/features/plans/PlansPage'))
// A-12 · the money ledger. 🔴 READ-ONLY BY CONSTRUCTION: `payments` has no
// INSERT, UPDATE or DELETE policy for anybody, so there is no write a screen
// could make — and it is empty because the only writer, the Stripe webhook Edge
// Function, has not been written.
const PaymentsPage = lazy(() => import('@/features/payments/PaymentsPage'))
// A-10 · migration 075. The promotion-package catalogue and the record of what
// has been bought. 🔴 IT IS NOT THE FRIDAY AUCTION — N20–N24, and `bid_status` /
// `bidding_round_status` still have no tables. ⚠️ It can edit packages and it can
// touch NO placement: `purchases` has no INSERT, UPDATE or DELETE policy for
// anybody, and since 075 its money and window are immutable by trigger as well.
const SponsoredPage = lazy(() => import('@/features/sponsored/SponsoredPage'))
// A-28 · migrations 079 / 084 / 086. The queue a human works to get money OUT of
// the platform. 🔴 IT SENDS NO MONEY: escrow was built WITHOUT Stripe Connect, so
// "Paid" is a person asserting they made a bank transfer elsewhere — which is why
// `review_payout_request()` refuses it without the bank's own reference. ⚠️ And it
// cannot hold a row yet: nothing in the applied schema inserts an `escrow_holds`
// row, so every balance is zero and `request_payout()` refuses every amount.
const PayoutsPage = lazy(() => import('@/features/payouts/PayoutsPage'))
const LoginPage = lazy(() => import('@/features/auth/LoginPage'))

/**
 * The modules that are routed but not yet implemented share one lazily loaded
 * placeholder chunk rather than a dozen near-identical files.
 */
function lazyPlaceholder(moduleId: string): ComponentType {
  return lazy(async () => {
    const { PlaceholderPage } = await import('@/components/common/PlaceholderPage')
    const Placeholder = (): ReactNode => <PlaceholderPage moduleId={moduleId} />
    Placeholder.displayName = `Placeholder(${moduleId})`
    return { default: Placeholder }
  })
}

const IMPLEMENTED: Record<string, ComponentType> = {
  dashboard: DashboardPage,
  users: UsersPage,
  businesses: BusinessesPage,
  categories: CategoriesPage,
  services: ServicesPage,
  topics: TopicsPage,
  daily: DailyPage,
  requests: RequestsPage,
  reviews: ReviewsPage,
  moderation: ModerationPage,
  verification: VerificationPage,
  claims: ClaimsPage,
  submissions: SubmissionsPage,
  templates: TemplatesPage,
  broadcasts: BroadcastsPage,
  translations: TranslationsPage,
  cms: CmsPage,
  settings: SettingsPage,
  featureFlags: FeatureFlagsPage,
  auditLog: AuditLogPage,
  analytics: AnalyticsPage,
  adminTeam: AdminTeamPage,
  support: SupportPage,
  disputes: DisputesPage,
  plans: PlansPage,
  payments: PaymentsPage,
  sponsored: SponsoredPage,
  payouts: PayoutsPage,
}

/** Resolved once at module scope — creating a `lazy()` during render would
 *  remount (and re-fetch) the chunk on every re-render. */
const PAGES: ReadonlyMap<string, ComponentType> = new Map(
  MODULES.map((module) => [module.id, IMPLEMENTED[module.id] ?? lazyPlaceholder(module.id)]),
)

/**
 * EVERY ROUTE IS GUARDED, INCLUDING THE ONES THAT DO NOT EXIST.
 *
 * `RequireAdmin` is a LAYOUT route wrapping `AdminShell`, so the guard sits
 * above the shell rather than inside each page. Three consequences, all of them
 * deliberate:
 *
 *   1. Adding a module to `MODULES` cannot accidentally ship an unguarded
 *      screen. There is no per-page opt-in to forget — the only way to route
 *      past this is to add a sibling `<Route>` outside it, which is a visible
 *      change in this file.
 *   2. The catch-all `*` is INSIDE the guard, so an unknown URL shows "not
 *      found" to an admin and the login form to everyone else. Outside it, a
 *      stranger could enumerate which routes 404 and which do not.
 *   3. The shell itself — sidebar, topbar, command palette — never renders for a
 *      non-admin, so the module list is not leaked by a screen that merely has
 *      no data in it.
 *
 * `/login` is the one route outside the guard, and it has its own (see
 * `RedirectIfSignedIn`).
 */
export function AppRoutes(): ReactNode {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route
          path="/login"
          element={
            <RedirectIfSignedIn>
              <LoginPage />
            </RedirectIfSignedIn>
          }
        />

        <Route element={<RequireAdmin />}>
          <Route element={<AdminShell />}>
            {MODULES.map((module) => {
              const Page = PAGES.get(module.id)
              if (!Page) return null
              return (
                <Route
                  key={module.id}
                  path={module.path}
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <Page />
                    </Suspense>
                  }
                />
              )
            })}
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Route>
      </Routes>
    </Suspense>
  )
}
